// ./src/utils/analytics/funnel.ts
//
// THE CREATE-EVENT FUNNEL (ROADMAP #62 P7) — every event name and parameter set the wizard sends, in ONE place, so a
// report built on them cannot be broken by a renamed string somewhere in a step.
//
//   wizard_start → wizard_step_view / wizard_step_complete (per step) → event_create (the essentials are in) →
//   wizard_finish  — or wizard_abandon (the host left; the last step they were on).
//   wizard_feature      — a feature switched on/off on the Features step, and where its card sat.
//   wizard_type_search  — a type search, once settled: a PICK sends the type, its rank and the CATALOG word that matched
//                         (never what was typed); a MISS (`matched_type: none`) sends the typed words, cleaned — the
//                         misses are the list of event types and synonyms to add next.
//   invitation_send / event_update_send — a real send (never a dry run), from the wizard's finish screen or the composer.
//
// ⓘ GA's own rules: names ≤ 40 characters, snake_case; parameters are only REPORTABLE once registered as custom
//   dimensions / metrics in the GA property (an owner step — listed in ROADMAP P7).
// ⚠ Pure, so `check-analytics` RUNS the builders.
import type { AnalyticsEventParams } from './types';
import { cleanSearchTerm } from './privacy';

export const WIZARD_EVENTS = {
  START: 'wizard_start',
  STEP_VIEW: 'wizard_step_view',
  STEP_COMPLETE: 'wizard_step_complete',
  FEATURE: 'wizard_feature',
  TYPE_SEARCH: 'wizard_type_search',
  EVENT_CREATE: 'event_create',
  FINISH: 'wizard_finish',
  ABANDON: 'wizard_abandon',
} as const;

/**
 * THE SIGN-UP FORM (ROADMAP #297, owner 2026-11-01: "a few users who spent ~1m on the page but no signups"):
 *   signup_start (first focus) → signup_field_done (per field, left filled in) → signup_submit (the button) →
 *   signup_error (each refusal) → `sign_up` — or signup_abandon (left; how far they got and the first field still empty).
 * ⚠ NEVER a value: progress is the NAMES of fields filled in, and a refusal the names of the fields it was about, never its
 *   message (the server's can echo what was typed). So a password or an email cannot reach GA from here.
 */
export const SIGNUP_EVENTS = {
  START: 'signup_start',
  FIELD_DONE: 'signup_field_done',
  SUBMIT: 'signup_submit',
  ERROR: 'signup_error',
  ABANDON: 'signup_abandon',
  EMAIL_HELP: 'signup_email_help',
} as const;

/**
 * Which form these events describe, on every one of them, so a report can compare before and after a change of fields.
 * 2 = email, password, birth, terms (owner, 2026-11-02); events with no version are the long form. Raise it when the form's
 * fields change.
 */
export const SIGNUP_FORM_VERSION = 2;

/** Where a refusal came from: this browser's checks, the server's answer, no answer, or the form's own settings read. */
export type SignupErrorSource = 'form' | 'server' | 'network' | 'settings';

/** How a run ended unfinished: the tab closed or reloaded, the form unmounted, or "Already have an account?". */
export type SignupAbandonHow = 'page_left' | 'navigated' | 'signin';

export interface SignupProgress {
  /** The fields this form requires right now, in screen order. */
  asked: string[];
  filled: string[];
}

/** What is asked and what is filled in, from presence alone — this takes booleans so that a value cannot get in. */
export const signupProgress = (p: {
  email: boolean;
  password: boolean;
  askBirth: boolean;
  birth: boolean;
  terms: boolean;
}): SignupProgress => {
  const fields: Array<[string, boolean, boolean]> = [
    ['email', true, p.email],
    ['password', true, p.password],
    ['birth', p.askBirth, p.birth],
    ['terms', true, p.terms],
  ];
  const asked = fields.filter(([, ask]) => ask);

  return { asked: asked.map(([name]) => name), filled: asked.filter(([, , has]) => has).map(([name]) => name) };
};

/**
 * The fields a refusal really named. ⚠ `getFormErrors` answers with EVERY known field, empty ones included, so reading its
 * keys reported all of them on any refusal.
 */
export const refusedFieldNames = (fields: Record<string, string | undefined>): string[] => Object.keys(fields).filter((name) => !!fields[name]);

/** The form's and the server's names for a field, as the funnel names it. Anything else is left out. */
const SIGNUP_ERROR_FIELDS: Record<string, string> = {
  companyName: 'company',
  accountType: 'account_type',
  username: 'username',
  email: 'email',
  password: 'password',
  confirmPassword: 'confirm_password',
  repeatPassword: 'confirm_password',
  birthYear: 'birth',
  birthMonth: 'birth',
  terms: 'terms',
};

export const signupErrorParams = (p: { source: SignupErrorSource; fields: string[]; general: boolean; code?: number; attempt: number }): AnalyticsEventParams => ({
  error_source: p.source,
  error_fields: Array.from(new Set(p.fields.map((f) => SIGNUP_ERROR_FIELDS[f]).filter(Boolean))).join(','),
  error_general: p.general,
  ...(p.code != null ? { error_code: p.code } : {}),
  attempt: p.attempt,
});

/**
 * One run of the form. ⓘ Plain code, not a hook, so `check-analytics` RUNS it; `useSignupFunnel` only decides when to call.
 * ⚠ ONE ending per run, an abandon only after a start, and each field reported once however often it is left.
 */
export const createSignupFunnel = (sendRaw: (name: string, params: AnalyticsEventParams) => void, now: () => number = Date.now) => {
  const send = (name: string, params: AnalyticsEventParams) => sendRaw(name, { ...params, form_version: SIGNUP_FORM_VERSION });
  let helpOpened = false;
  let startedAt: number | null = null;
  let ended = false;
  let attempts = 0;
  let refusals = 0;
  const reported = new Set<string>();
  const msOnForm = () => (startedAt == null ? 0 : elapsedMs(startedAt, now()));

  const touched = (p: SignupProgress): void => {
    if (startedAt != null || ended) {
      return;
    }

    startedAt = now();
    send(SIGNUP_EVENTS.START, { fields_total: p.asked.length });
  };

  const left = (p: SignupProgress): void => {
    if (startedAt == null || ended) {
      return;
    }

    for (const field of p.filled) {
      if (!reported.has(field)) {
        reported.add(field);
        send(SIGNUP_EVENTS.FIELD_DONE, { field, fields_done: p.filled.length, fields_total: p.asked.length, ms_on_form: msOnForm() });
      }
    }
  };

  return {
    touched,
    left,
    /** The button was pressed (or Enter), whether or not what follows accepts it. */
    submitted: (p: SignupProgress): void => {
      touched(p);
      left(p);
      attempts += 1;
      send(SIGNUP_EVENTS.SUBMIT, { fields_done: p.filled.length, fields_total: p.asked.length, attempt: attempts, ms_on_form: msOnForm() });
    },
    refused: (source: SignupErrorSource, fields: string[], general: boolean, code?: number): void => {
      refusals += 1;
      send(SIGNUP_EVENTS.ERROR, signupErrorParams({ source, fields, general, code, attempt: attempts }));
    },
    /** "Which email should I use?" opened: once per run, however often it is toggled. */
    emailHelpOpened: (): void => {
      if (!helpOpened) {
        helpOpened = true;
        send(SIGNUP_EVENTS.EMAIL_HELP, {});
      }
    },
    /** The account exists: `sign_up` reports it, and nothing after this is an abandon. */
    finished: (): void => {
      ended = true;
    },
    abandoned: (how: SignupAbandonHow, p: SignupProgress): void => {
      if (ended || startedAt == null) {
        return;
      }

      ended = true;
      send(SIGNUP_EVENTS.ABANDON, {
        how,
        fields_done: p.filled.length,
        fields_total: p.asked.length,
        first_missing: p.asked.find((f) => !p.filled.includes(f)) ?? 'none',
        attempts,
        errors: refusals,
        ms_on_form: msOnForm(),
      });
    },
    /** Back from the back/forward cache: the same run returns, so its `page_left` ending is lifted. */
    revived: (): void => {
      ended = false;
    },
  };
};

/**
 * THE ACCOUNT ONBOARDING (owner, 2026-11-02: "proper GA events … what users do on the signup and the onboarding"):
 *   onboarding_start → onboarding_step_view → onboarding_step_complete | onboarding_step_skip (per step) → onboarding_finish —
 *   or onboarding_abandon (`how`: the "later" button, the tab closed, or the page left). onboarding_error is a refused save.
 * ⚠ Never a value, as above: step names and field names. The one choice sent is `account_type`, a word from our own list
 *   (`config/accountTypes`), never anything typed. ⚠ A detour (one step, then back) is not a run: it sends nothing.
 * ⓘ Going to Stripe leaves the page, so `how: page_left` with `last_step: payments` may be someone coming straight back;
 *   their return is a new run with `resumed: true`.
 */
export const ONBOARDING_EVENTS = {
  START: 'onboarding_start',
  STEP_VIEW: 'onboarding_step_view',
  STEP_COMPLETE: 'onboarding_step_complete',
  STEP_SKIP: 'onboarding_step_skip',
  ERROR: 'onboarding_error',
  FINISH: 'onboarding_finish',
  ABANDON: 'onboarding_abandon',
} as const;

export type OnboardingAbandonHow = 'later' | 'page_left' | 'navigated';

export interface OnboardingProgress {
  /** The steps offered and how many are done now. */
  stepsTotal: number;
  stepsDone: number;
}

/** The server's names for a field of a step, as the funnel names it. Anything else is left out. */
const ONBOARDING_ERROR_FIELDS: Record<string, string> = {
  name: 'company',
  accountType: 'account_type',
};

/** Only a word shaped like our own list's: what cannot be one of them is not sent. */
const accountTypeParam = (accountType: string | null | undefined): AnalyticsEventParams =>
  accountType && /^[a-z_]{1,32}$/.test(accountType) ? { account_type: accountType } : {};

/** One run of the onboarding. ⓘ Plain code, so `check-analytics` RUNS it. ⚠ ONE ending per run; each step views once in a row. */
export const createOnboardingFunnel = (send: (name: string, params: AnalyticsEventParams) => void, now: () => number = Date.now) => {
  let startedAt: number | null = null;
  let stepAt = 0;
  let ended = false;
  let viewed: { step: string; index: number; count: number } | null = null;
  const stepParams = () => (viewed ? { step: viewed.step, step_index: viewed.index + 1, step_count: viewed.count } : {});

  return {
    /** A step is on screen; the first one starts the run. `required` = it has no skip. */
    viewed: (p: { step: string; index: number; count: number; required: boolean; resumed: boolean }): void => {
      if (ended) {
        return;
      }

      if (startedAt == null) {
        startedAt = now();
        send(ONBOARDING_EVENTS.START, { steps_total: p.count, resumed: p.resumed });
      }

      const sameStep = viewed?.step === p.step;

      viewed = { step: p.step, index: p.index, count: p.count };

      // ⓘ The same step with a longer list (a venue account grows a step) is no new view.
      if (!sameStep) {
        stepAt = now();
        send(ONBOARDING_EVENTS.STEP_VIEW, { ...stepParams(), required: p.required });
      }
    },
    completed: (accountType?: string | null): void => {
      if (!ended && viewed) {
        send(ONBOARDING_EVENTS.STEP_COMPLETE, { ...stepParams(), ms_on_step: elapsedMs(stepAt, now()), ...accountTypeParam(accountType) });
      }
    },
    skipped: (): void => {
      if (!ended && viewed) {
        send(ONBOARDING_EVENTS.STEP_SKIP, { ...stepParams(), ms_on_step: elapsedMs(stepAt, now()) });
      }
    },
    refused: (fields: string[], general: boolean, code?: number): void => {
      if (!ended && viewed) {
        send(ONBOARDING_EVENTS.ERROR, {
          step: viewed.step,
          error_fields: Array.from(new Set(fields.map((f) => ONBOARDING_ERROR_FIELDS[f]).filter(Boolean))).join(','),
          error_general: general,
          ...(code != null ? { error_code: code } : {}),
        });
      }
    },
    finished: (p: OnboardingProgress): void => {
      if (!ended && startedAt != null) {
        ended = true;
        send(ONBOARDING_EVENTS.FINISH, { steps_done: p.stepsDone, steps_total: p.stepsTotal, ms_on_onboarding: elapsedMs(startedAt, now()) });
      }
    },
    abandoned: (how: OnboardingAbandonHow, p: OnboardingProgress): void => {
      if (!ended && startedAt != null) {
        ended = true;
        send(ONBOARDING_EVENTS.ABANDON, {
          how,
          last_step: viewed?.step ?? 'none',
          steps_done: p.stepsDone,
          steps_total: p.stepsTotal,
          ms_on_onboarding: elapsedMs(startedAt, now()),
        });
      }
    },
    /** Back from the back/forward cache: the same run returns, so its `page_left` ending is lifted. */
    revived: (): void => {
      ended = false;
    },
  };
};

export const GUEST_MESSAGE_EVENTS = {
  invitation: 'invitation_send',
  event_update: 'event_update_send',
} as const;

/** Where a feature card sat when it was switched: the type's suggestions, the type's other offers, or "More features". */
export type FeatureSource = 'suggested' | 'offered' | 'more';

/** Which screen sent guests a message. ⚠ Required wherever a send is made, so a new caller cannot go unattributed. */
export type SendSource = 'wizard' | 'composer';

/** Whole milliseconds since `from`; never negative (a clock change must not report a minus). */
export const elapsedMs = (from: number, now: number): number => Math.max(0, Math.round(now - from));

export const wizardStartParams = (p: { resumed: boolean; deepLink: boolean }): AnalyticsEventParams => ({
  resumed: p.resumed,
  deep_link: p.deepLink,
});

/** ⓘ `step_index` is 1-based and counts the steps THIS draft shows, which change with its features. */
export const wizardStepParams = (p: { step: string; index: number; count: number; ms?: number }): AnalyticsEventParams => ({
  step: p.step,
  step_index: p.index + 1,
  step_count: p.count,
  ...(p.ms != null ? { ms_on_step: p.ms } : {}),
});

export const wizardFeatureParams = (p: { feature: string; on: boolean; source: FeatureSource }): AnalyticsEventParams => ({
  feature: p.feature,
  on: p.on,
  source: p.source,
});

/**
 * A type search, once it SETTLES.
 *   - A PICK (`matchedType` set): the type, where it sat in the list, and the catalog word that matched it — a synonym, or
 *     `name` when the type's own name did. ⚠ Never the typed words: "smith wedding" finds Wedding, and the name is the
 *     host's guest, not ours to send.
 *   - A MISS: the typed words, cleaned (`cleanSearchTerm`) — null, and nothing sent, when nothing safe is left.
 */
export const wizardTypeSearchParams = (p: {
  term: string;
  matchedType: string | null;
  matchedWord: string | null;
  rank: number | null;
  results: number;
}): AnalyticsEventParams | null => {
  if (p.matchedType) {
    return {
      matched_type: p.matchedType,
      matched_word: p.matchedWord ?? 'name',
      results: p.results,
      ...(p.rank != null ? { rank: p.rank } : {}),
    };
  }

  const term = cleanSearchTerm(p.term);

  if (!term) {
    return null;
  }

  return { term, matched_type: 'none', results: p.results };
};

export const eventCreateParams = (p: {
  step: string;
  ms: number;
  eventMode: string | null;
  eventType: string | null;
  parentType: string | null;
  eventFormat: string | null;
  features: number;
  /** #139 — the run took a quick path (Type → Basics → Media). */
  quick: boolean;
  /** #148 — which one: `headcount` or `tickets`; null on the full wizard (sent as `none`). */
  quickKind: string | null;
}): AnalyticsEventParams => ({
  step: p.step,
  ms_total: p.ms,
  event_mode: p.eventMode ?? 'none',
  event_type: p.eventType ?? 'none',
  parent_type: p.parentType ?? 'none',
  event_format: p.eventFormat ?? 'none',
  features: p.features,
  quick: p.quick,
  quick_kind: p.quickKind ?? 'none',
});

export const wizardEndParams = (p: {
  step: string;
  ms: number;
  created: boolean;
  features: number;
  quick: boolean;
  quickKind: string | null;
}): AnalyticsEventParams => ({
  step: p.step,
  ms_total: p.ms,
  event_created: p.created,
  features: p.features,
  quick: p.quick,
  quick_kind: p.quickKind ?? 'none',
});

export const guestMessageSendParams = (p: {
  channel: string;
  sent: number;
  failed: number;
  from: SendSource;
}): AnalyticsEventParams => ({
  channel: p.channel,
  count: p.sent,
  failed: p.failed,
  from: p.from,
});
